
import { supabase } from '@/integrations/supabase/client';

interface ErrorCacheEntry {
  timestamp: number;
  count: number;
}

class ErrorManagementService {
  private static errorCache: Map<string, ErrorCacheEntry> = new Map();
  private static readonly ERROR_CACHE_TTL = 1000 * 60 * 5; // 5 minutes
  private static readonly ERROR_THRESHOLD = 3; // Max errors of same type in TTL
  private static readonly BACKOFF_BASE = 2; // Base for exponential backoff
  private static securityAlertCount = 0;
  private static lastSecurityAlertTime = 0;

  static generateErrorFingerprint(error: any): string {
    const message = error?.message || 'Unknown error';
    const code = error?.code || '';
    const path = error?.path || '';
    return `${code}:${message}:${path}`;
  }

  static shouldReportError(error: any): boolean {
    const fingerprint = this.generateErrorFingerprint(error);
    const now = Date.now();
    const entry = this.errorCache.get(fingerprint);

    // Clean up old entries
    if (entry && now - entry.timestamp > this.ERROR_CACHE_TTL) {
      this.errorCache.delete(fingerprint);
      return true;
    }

    // Update or create entry
    if (!entry) {
      this.errorCache.set(fingerprint, { timestamp: now, count: 1 });
      return true;
    }

    // Increment count
    entry.count++;
    
    // Check if security alert should be triggered
    if (this.detectPotentialSecurityIssue(error)) {
      this.securityAlertCount++;
      
      // Limit security alerts to prevent flooding
      if (now - this.lastSecurityAlertTime > 60000) { // 1 minute between alerts
        this.lastSecurityAlertTime = now;
        this.logSecurityAlert(error);
      }
    }
    
    // Only report if under threshold
    return entry.count <= this.ERROR_THRESHOLD;
  }

  static detectPotentialSecurityIssue(error: any): boolean {
    const errorString = JSON.stringify(error).toLowerCase();
    
    // Check for common security-related error patterns
    const securityPatterns = [
      'sql inject', 'xss', 'csrf', 'cross site', 
      'authorization', 'forbidden', 'permission', 'exploit',
      'attack', 'unauthorized', 'token invalid', 'jwt',
      'cors', 'origin', 'allowed'
    ];
    
    return securityPatterns.some(pattern => errorString.includes(pattern));
  }

  static async logSecurityAlert(error: any): Promise<void> {
    try {
      // Check if we have a valid session before logging to database
      const { data } = await supabase.auth.getSession();
      
      const { error: insertError } = await supabase
        .from('error_logs')
        .insert({
          error_type: 'security_alert',
          error_message: error?.message || 'Potential security issue detected',
          stack_trace: error?.stack,
          metadata: {
            details: error?.details,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            alertCount: this.securityAlertCount
          },
          route: window.location.pathname
        });

      if (insertError) {
        console.error('Failed to log security alert:', insertError);
      }
    } catch (loggingError) {
      console.error('Failed to log security alert:', loggingError);
    }
  }

  static async handleQueryError(error: any): Promise<void> {
    // Always log to console for debugging
    console.error('Query error:', error);
    
    if (this.shouldReportError(error)) {
      try {
        // Check if we have a valid session before logging to database
        const { data } = await supabase.auth.getSession();
        
        if (!data.session) {
          console.log('No active session, skipping error logging to database');
          return;
        }
        
        const { error: insertError } = await supabase
          .from('error_logs')
          .insert({
            error_type: 'query_error',
            error_message: error?.message || 'Unknown error',
            stack_trace: error?.stack,
            metadata: {
              code: error?.code,
              path: error?.path,
              details: error?.details,
              header_error: error?.name === 'HeadersError' ? true : undefined,
              xhr_error: error?.name === 'TypeError' && error?.message?.includes('NetworkError') ? true : undefined,
              cors_error: error?.message?.includes('CORS') || error?.message?.includes('cross-origin') ? true : undefined
            },
            route: window.location.pathname
          });

        if (insertError) {
          console.error('Failed to log error:', insertError);
        }
      } catch (loggingError) {
        console.error('Failed to log error:', loggingError);
      }
    }
  }

  static async handleSubscriptionError(error: any): Promise<void> {
    // Always log to console for debugging
    console.error('Subscription error:', error);
    
    if (this.shouldReportError(error)) {
      try {
        // Check if we have a valid session before logging to database
        const { data } = await supabase.auth.getSession();
        
        if (!data.session) {
          console.log('No active session, skipping error logging to database');
          return;
        }
        
        const { error: insertError } = await supabase
          .from('error_logs')
          .insert({
            error_type: 'subscription_error',
            error_message: error?.message || 'Unknown error',
            stack_trace: error?.stack,
            metadata: {
              code: error?.code,
              path: error?.path,
              details: error?.details,
              channel: error?.channel
            },
            route: window.location.pathname
          });

        if (insertError) {
          console.error('Failed to log error:', insertError);
        }
      } catch (loggingError) {
        console.error('Failed to log error:', loggingError);
      }
    }
  }

  static async handleAuthError(error: any): Promise<void> {
    // Always log to console for debugging
    console.error('Auth error:', error);
    
    if (this.shouldReportError(error)) {
      try {
        const { error: insertError } = await supabase
          .from('error_logs')
          .insert({
            error_type: 'auth_error',
            error_message: error?.message || 'Unknown error',
            stack_trace: error?.stack,
            metadata: {
              code: error?.code,
              path: error?.path,
              details: error?.details
            },
            route: window.location.pathname
          });

        if (insertError) {
          console.error('Failed to log error:', insertError);
        }
      } catch (loggingError) {
        console.error('Failed to log error:', loggingError);
      }
    }
  }

  static calculateBackoffDelay(retryCount: number, maxDelay = 10000): number {
    return Math.min(
      1000 * Math.pow(this.BACKOFF_BASE, retryCount),
      maxDelay
    );
  }

  static cleanupErrorCache(): void {
    const now = Date.now();
    for (const [key, entry] of this.errorCache.entries()) {
      if (now - entry.timestamp > this.ERROR_CACHE_TTL) {
        this.errorCache.delete(key);
      }
    }
  }

  static sanitizeInput(input: string): string {
    if (!input) return input;
    
    // Basic sanitization for XSS prevention
    return input
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }

  static validateInput(input: any, type: 'string' | 'number' | 'email' | 'uuid'): boolean {
    if (input === null || input === undefined) return false;
    
    switch (type) {
      case 'string':
        return typeof input === 'string';
      case 'number':
        return !isNaN(Number(input));
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
      case 'uuid':
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(input);
      default:
        return false;
    }
  }
}

export default ErrorManagementService;
